import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DeliveryOrderPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:9001';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const existingScript = document.getElementById('razorpay-checkout-js');
    if (existingScript) {
      existingScript.onload = () => resolve(true);
      existingScript.onerror = () => resolve(false);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

let appCurrencySymbol = '₹';
export function setAppCurrencySymbol(sym) {
  if (sym) appCurrencySymbol = sym;
}

function formatRupee(val, symbol = null) {
  const sym = symbol !== null ? symbol : appCurrencySymbol;
  const num = parseFloat(val || 0);
  if (isNaN(num)) return `${sym}0`;
  const rounded = Number(Math.round(Number(num + 'e+2')) + 'e-2');
  return `${sym}${rounded}`;
}

function formatDeliveryTimeRange(mins) {
  const baseTime = parseInt(mins, 10);
  const time = !isNaN(baseTime) && baseTime > 0 ? baseTime : 30;
  return `${time}-${time + 5} min`;
}

function extractBusinessPayload(payload) {
  const b = payload.business || {};
  return {
    ...b,
    banners: payload.banners || b.banners || [],
    discount_rule: payload.discount_rule || b.discount_rule || null,
    delivery_price: payload.delivery_price !== undefined ? payload.delivery_price : b.delivery_price,
    delivery_time_in_minutes: payload.delivery_time_in_minutes !== undefined ? payload.delivery_time_in_minutes : (b.delivery_time_in_minutes !== undefined ? b.delivery_time_in_minutes : 30),
    min_delivery_amount: payload.min_delivery_amount !== undefined ? payload.min_delivery_amount : b.min_delivery_amount,
    taxes: payload.taxes || b.taxes || [],
    total_tax_percentage: payload.total_tax_percentage !== undefined ? payload.total_tax_percentage : (b.total_tax_percentage || 0),
    pos_integrated: payload.pos_integrated !== undefined ? payload.pos_integrated : b.pos_integrated,
    razorpay_enabled: payload.razorpay_enabled !== undefined ? payload.razorpay_enabled : (b.razorpay_enabled || false),
    razorpay_key_id: payload.razorpay_key_id !== undefined ? payload.razorpay_key_id : (b.razorpay_key_id || null),
    is_open: payload.is_open !== undefined ? payload.is_open : (b.is_open !== undefined ? b.is_open : true),
    status_message: payload.status_message || b.status_message || 'Open',
    next_open_at: payload.next_open_at || b.next_open_at || null,
    currency_code: payload.currency_code || b.currency_code || 'INR',
    currency_symbol: payload.currency_symbol || b.currency_symbol || '₹',
    timezone: payload.timezone || b.timezone || 'Asia/Kolkata',
    delivery_radius: payload.delivery_radius !== undefined ? payload.delivery_radius : (b.delivery_radius || 15.0),
    max_delivery_radius_km: payload.max_delivery_radius_km !== undefined ? payload.max_delivery_radius_km : (b.max_delivery_radius_km || 15.0),
    distance_km: payload.distance_km !== undefined ? payload.distance_km : (b.distance_km ?? null),
    is_serviceable: payload.is_serviceable !== undefined ? payload.is_serviceable : (b.is_serviceable !== undefined ? b.is_serviceable : true),
    unserviceable_reason: payload.unserviceable_reason || b.unserviceable_reason || null,
  };
}

function formatNextOpenText(nextOpenAt, statusMsg) {
  if (!nextOpenAt) {
    return statusMsg || 'Outlet is currently closed';
  }
  const clean = String(nextOpenAt).trim();
  if (/^opens/i.test(clean)) {
    return clean;
  }
  if (/^tomorrow/i.test(clean)) {
    return `Opens ${clean}`;
  }
  return `Opens at ${clean}`;
}

function renderDietaryIndicator(item) {
  if (!item) return null;
  const dietary = (item.dietary_type || '').toLowerCase();
  const attrId = String(item.item_attributeid || item.attribute_code || '');

  if (dietary === 'egg' || attrId === '24') {
    return (
      <div className="egg-symbol" title="Egg">
        <div className="egg-dot" />
      </div>
    );
  }
  if (dietary === 'other' || attrId === '5') {
    return null;
  }
  if (dietary === 'non_veg' || dietary === 'non-veg' || attrId === '2') {
    return (
      <div className="nonveg-symbol" title="Non-Veg">
        <div className="nonveg-dot" />
      </div>
    );
  }
  if (dietary === 'veg' || attrId === '1') {
    return (
      <div className="veg-symbol" title="Veg">
        <div className="veg-dot" />
      </div>
    );
  }
  if (item.is_veg === false) {
    return (
      <div className="nonveg-symbol" title="Non-Veg">
        <div className="nonveg-dot" />
      </div>
    );
  }
  if (item.is_veg === true) {
    return (
      <div className="veg-symbol" title="Veg">
        <div className="veg-dot" />
      </div>
    );
  }
  return null;
}

function getItemPricingInfo(item) {
  if (!item) return { price: 0, markupPrice: null, discountPercentage: 0, hasDiscount: false };
  let price = parseFloat(item.price) || 0;
  let markupPrice = item.markup_price !== null && item.markup_price !== undefined ? parseFloat(item.markup_price) : null;
  let discountPercentage = parseInt(item.discount_percentage, 10) || 0;

  if (item.variants && item.variants.length > 0) {
    const sorted = [...item.variants].sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
    if (sorted[0] && sorted[0].price !== undefined) {
      price = parseFloat(sorted[0].price) || 0;
      if (sorted[0].markup_price !== null && sorted[0].markup_price !== undefined) {
        markupPrice = parseFloat(sorted[0].markup_price);
      }
      if (sorted[0].discount_percentage !== undefined && sorted[0].discount_percentage !== null) {
        discountPercentage = parseInt(sorted[0].discount_percentage, 10) || 0;
      }
    }
  }

  if (markupPrice && markupPrice > price && !discountPercentage) {
    discountPercentage = Math.round(((markupPrice - price) / markupPrice) * 100);
  } else if (discountPercentage > 0 && (!markupPrice || markupPrice <= price)) {
    markupPrice = Math.round((price / (1 - discountPercentage / 100)) * 100) / 100;
  }

  const hasDiscount = discountPercentage > 0 && markupPrice !== null && markupPrice > price;

  return {
    price,
    markupPrice,
    discountPercentage,
    hasDiscount,
  };
}

function getHeaderDeliveryAddress(addr) {
  if (!addr) return 'Add Delivery Address';
  const type = (addr.address_type || '').trim();
  const label = (addr.address_type_label || '').trim();

  if (type || label) {
    if (type && label && type.toLowerCase() !== label.toLowerCase()) {
      return `${label} (${type})`;
    }
    return label || type;
  }

  if (addr.house_number) {
    return `Flat ${addr.house_number}`;
  }

  if (addr.address) {
    return addr.address;
  }

  return 'Saved Address';
}

function getFormattedAddress(addr) {
  if (!addr) return '';
  const typeBadge =
    addr.address_type === 'HOME'
      ? '🏠 Home'
      : addr.address_type === 'WORK'
      ? '🏢 Work'
      : addr.address_type_label
      ? `📍 ${addr.address_type_label}`
      : '';

  const parts = [
    addr.house_number ? `Flat ${addr.house_number}` : '',
    addr.building_name,
    addr.address,
    addr.landmark_area,
    addr.city,
    addr.in_pin_code,
  ].filter(Boolean);

  const mainAddr = parts.length > 0 ? parts.join(', ') : 'Saved Address';
  return typeBadge ? `${typeBadge} - ${mainAddr}` : mainAddr;
}

export default function DeliveryOrderPage() {
  const { brand_id, business_id, customer_id } = useParams();
  const [searchParams] = useSearchParams();

  const effectiveBrandIdParam = brand_id || searchParams.get('brand_id') || null;
  const effectiveCustomerId = customer_id || searchParams.get('customer_id') || null;
  const initialBusinessId = business_id || searchParams.get('business_id') || null;

  // Core Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [business, setBusiness] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [deliveryType, setDeliveryType] = useState('delivery'); // 'delivery' or 'pickup'
  const [menu, setMenu] = useState([]);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);

  // Multi-outlet & Brand Selection States
  const [effectiveBrandId, setEffectiveBrandId] = useState(effectiveBrandIdParam);
  const [selectedBusinessId, setSelectedBusinessId] = useState(initialBusinessId);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [selectedPickupLocation, setSelectedPickupLocation] = useState(null);
  const [switchingLocation, setSwitchingLocation] = useState(false);
  const [modalTab, setModalTab] = useState('delivery'); // 'delivery' or 'pickup' inside address modal

  // Search & Navigation & Description Toggles
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  // Modal Parent Category Accordion States (Floating Menu Popover)
  const [modalExpandedParents, setModalExpandedParents] = useState({});

  const isModalParentExpanded = (parentId) => {
    if (searchQuery && searchQuery.trim().length > 0) return true;
    return !!modalExpandedParents[parentId];
  };

  const toggleModalParent = (parentId) => {
    setModalExpandedParents((prev) => ({
      ...prev,
      [parentId]: !prev[parentId],
    }));
  };

  // Compute total items across parent and standalone categories
  const totalMenuItemsCount = useMemo(() => {
    if (!menu || menu.length === 0) return 0;
    return menu.reduce((total, entry) => {
      if (entry.type === 'parent_category' && entry.categories) {
        return total + entry.categories.reduce((subTot, c) => subTot + (c.items?.length || 0), 0);
      }
      return total + (entry.items?.length || 0);
    }, 0);
  }, [menu]);

  // Toggle Category Menu Popover with auto-expansion of current active parent
  const handleToggleCategoryMenu = () => {
    setShowCategoryBook((prev) => {
      const nextState = !prev;
      if (nextState) {
        if (activeCategory && menu) {
          for (const entry of menu) {
            if (entry.type === 'parent_category' && entry.categories) {
              const hasChild = entry.categories.some((c) => String(c.id) === String(activeCategory));
              if (hasChild) {
                setModalExpandedParents((mPrev) => ({ ...mPrev, [entry.id]: true }));
                break;
              }
            }
          }
        }
      }
      return nextState;
    });
  };

  const getFirstCategoryId = (menuList) => {
    if (!menuList || menuList.length === 0) return null;
    const first = menuList[0];
    if (first.type === 'parent_category' && first.categories && first.categories.length > 0) {
      return first.categories[0].id;
    }
    return first.id;
  };
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollY = useRef(0);

  // Banner State
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Cart State
  const [cart, setCart] = useState([]);
  const [orderNotes, setOrderNotes] = useState('');
  const [showOrderNotes, setShowOrderNotes] = useState(false);
  const [useLoyalty, setUseLoyalty] = useState(true);

  // Modals
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showNewAddressModal, setShowNewAddressModal] = useState(false);
  const [showCategoryBook, setShowCategoryBook] = useState(false);
  const [showItemCustomizer, setShowItemCustomizer] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);

  // Customizer Item State
  const [customizingItem, setCustomizingItem] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [highlightedGroupId, setHighlightedGroupId] = useState(null);
  const highlightTimeoutRef = useRef(null);

  // 2-Step New Address Form State
  const [addressStep, setAddressStep] = useState('map'); // 'map' (Step 1) or 'details' (Step 2)
  const [newAddressForm, setNewAddressForm] = useState({
    house_number: '',
    floor_number: '',
    tower_number: '',
    building_name: '',
    address: '',
    landmark_area: '',
    city: '',
    state: '',
    in_pin_code: '',
    lat: null,
    lon: null,
    address_type: 'HOME', // 'HOME', 'WORK', 'OTHER'
    address_type_label: 'Home',
    recipient_name: '',
    recipient_phone: '',
  });
  const [isEditingRecipient, setIsEditingRecipient] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationNotification, setLocationNotification] = useState('');

  // Map Refs
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Order Placement & Razorpay Payment
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  // Helper to check if a cart item is valid in a given menu
  const isItemValidInMenu = (cartItem, currentMenu) => {
    if (!currentMenu || currentMenu.length === 0) return false;
    for (const entry of currentMenu) {
      const itemsToCheck =
        entry.type === 'parent_category' && entry.categories
          ? entry.categories.flatMap((c) => c.items || [])
          : (entry.items || []);
      for (const prod of itemsToCheck) {
        if (prod.id === cartItem.product_id) {
          if (prod.in_stock === false) return false;
          if (cartItem.variant_id && prod.variants && prod.variants.length > 0) {
            const varMatch = prod.variants.find((v) => v.id === cartItem.variant_id);
            if (!varMatch) return false;
          }
          return true;
        }
      }
    }
    return false;
  };

  // Re-validate cart items against revised menu
  const validateAndUpdateCart = useCallback((newMenu) => {
    setCart((prevCart) =>
      prevCart.map((item) => ({
        ...item,
        isUnavailable: !isItemValidInMenu(item, newMenu),
      }))
    );
  }, []);

  // 1. Initial Data Fetch
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let endpoint = '';
      if (effectiveBrandIdParam) {
        endpoint = `${API_BASE}/business/public/brand_delivery_info?brand_id=${effectiveBrandIdParam}&customer_id=${effectiveCustomerId}`;
      } else {
        endpoint = `${API_BASE}/business/public/delivery_info?business_id=${initialBusinessId}&customer_id=${effectiveCustomerId}`;
      }

      const res = await fetch(endpoint);
      if (!res.ok) {
        throw new Error('Failed to load menu details.');
      }
      const data = await res.json();
      const payload = data.data || data;

      const mergedBusiness = extractBusinessPayload(payload);
      setAppCurrencySymbol(mergedBusiness.currency_symbol);
      setBusiness(mergedBusiness);
      setCustomer(payload.customer);
      setAddresses(payload.addresses || []);

      const brandIdResolved = payload.brand_id || effectiveBrandIdParam;
      if (brandIdResolved) {
        setEffectiveBrandId(brandIdResolved);
      }

      const bizIdResolved = payload.selected_business_id || payload.business?.id || initialBusinessId;
      if (bizIdResolved) {
        setSelectedBusinessId(bizIdResolved);
      }

      const locations = payload.pickup_locations || payload.business?.pickup_locations || [];
      setPickupLocations(locations);

      if (payload.addresses && payload.addresses.length > 0) {
        if (payload.latest_address_id) {
          const matched = payload.addresses.find((a) => a.id === payload.latest_address_id);
          setSelectedAddress(matched || payload.addresses[0]);
        } else {
          setSelectedAddress(payload.addresses[0]);
        }
      }

      if (locations.length > 0) {
        const matchedLoc = locations.find((l) => l.id === bizIdResolved);
        setSelectedPickupLocation(matchedLoc || locations[0]);
      }

      const initialMenu = payload.menu || [];
      setMenu(initialMenu);
      setLoyaltyPoints(payload.loyalty_points || 0);

      if (initialMenu.length > 0) {
        setActiveCategory(getFirstCategoryId(initialMenu));
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [effectiveBrandIdParam, effectiveCustomerId, initialBusinessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Set Theme Color CSS Variables
  useEffect(() => {
    if (business && business.brand_theme_color) {
      document.documentElement.style.setProperty(
        '--brand-color',
        business.brand_theme_color
      );
    }
  }, [business]);

  // Auto-redirect to WhatsApp countdown after successful payment
  useEffect(() => {
    let timer;
    if (orderSuccess && confirmedOrder) {
      setRedirectCountdown(10);
      timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            const waPhone = business?.whatsapp_phone_number
              ? String(business.whatsapp_phone_number).replace(/\D/g, '')
              : '';
            if (waPhone) {
              window.location.href = `https://wa.me/${waPhone}`;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [orderSuccess, confirmedOrder, business]);

  // Scroll listener: Hide address bar as soon as user starts scrolling (>10px)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 10) {
        setHeaderHidden(true);
      } else {
        setHeaderHidden(false);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when any modal is open
  useEffect(() => {
    const isAnyModalOpen =
      showAddressModal ||
      showNewAddressModal ||
      showCategoryBook ||
      showItemCustomizer ||
      showCartModal ||
      Boolean(selectedProductDetail);

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [
    showAddressModal,
    showNewAddressModal,
    showCategoryBook,
    showItemCustomizer,
    showCartModal,
    selectedProductDetail,
  ]);

  // Active Banners
  const banners = (business?.banners || []).filter(
    (b) => b && b.is_active !== false && (!b.end_datetime || new Date(b.end_datetime) > new Date())
  );

  // Auto-advance Banners every 4 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [banners.length]);

  // Banner Click Handler (Requirement 9: Scroll to category if click_path has category)
  const handleBannerClick = (banner) => {
    if (!banner || !banner.click_path) return;
    const path = banner.click_path;
    const match = path.match(/[?&]category=([^&]+)/i);
    if (match) {
      const targetCatStr = decodeURIComponent(match[1]).trim().toLowerCase();
      let matchedCategory = null;
      let matchedParentId = null;
      for (const entry of menu) {
        if (entry.type === 'parent_category' && entry.categories) {
          const found = entry.categories.find((c) => {
            const cName = (c.name || '').trim().toLowerCase();
            const cId = String(c.id).trim().toLowerCase();
            return cName === targetCatStr || cId === targetCatStr || cName.includes(targetCatStr) || targetCatStr.includes(cName);
          });
          if (found) {
            matchedCategory = found;
            matchedParentId = entry.id;
            break;
          }
        }
        const cName = (entry.name || '').trim().toLowerCase();
        const cId = String(entry.id).trim().toLowerCase();
        if (cName === targetCatStr || cId === targetCatStr || cName.includes(targetCatStr) || targetCatStr.includes(cName)) {
          matchedCategory = entry;
          break;
        }
      }
      if (matchedCategory) {
        setActiveCategory(matchedCategory.id);
        if (matchedParentId) {
          setModalExpandedParents((prev) => ({ ...prev, [matchedParentId]: true }));
        }
        setTimeout(() => {
          const catElement = document.getElementById(`cat-block-${matchedCategory.id}`);
          if (catElement) {
            catElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 50);
        return;
      }
    }

    if (path.startsWith('http://') || path.startsWith('https://')) {
      window.open(path, '_blank');
    }
  };

  // Action: Select Address (Delivery mode)
  const handleSelectAddress = async (addr) => {
    setSelectedAddress(addr);
    setDeliveryType('delivery');
    setShowAddressModal(false);

    const brandToUse = effectiveBrandId || effectiveBrandIdParam;
    if (brandToUse) {
      setSwitchingLocation(true);
      try {
        const res = await fetch(
          `${API_BASE}/business/public/brand_address_delivery_info?brand_id=${brandToUse}&address_id=${addr.id}`
        );
        if (!res.ok) throw new Error('Failed to update menu for selected address.');
        const data = await res.json();
        const payload = data.data || data;

        if (payload.business || payload.banners) {
          const merged = extractBusinessPayload(payload);
          setAppCurrencySymbol(merged.currency_symbol);
          setBusiness(merged);
        }
        if (payload.selected_business_id) setSelectedBusinessId(payload.selected_business_id);
        if (payload.pickup_locations) setPickupLocations(payload.pickup_locations);
        if (payload.loyalty_points !== undefined) setLoyaltyPoints(payload.loyalty_points);

        const newMenu = payload.menu || [];
        setMenu(newMenu);
        validateAndUpdateCart(newMenu);
        if (newMenu.length > 0) setActiveCategory(getFirstCategoryId(newMenu));
      } catch (err) {
        console.error(err);
        alert('Could not update menu: ' + err.message);
      } finally {
        setSwitchingLocation(false);
      }
    }
  };

  // Action: Select Pickup Location (Pickup mode)
  const handleSelectPickupLocation = async (loc) => {
    setSelectedPickupLocation(loc);
    setSelectedBusinessId(loc.id);
    setDeliveryType('pickup');
    setShowAddressModal(false);

    setSwitchingLocation(true);
    try {
      const res = await fetch(
        `${API_BASE}/business/public/delivery_info?business_id=${loc.id}&customer_id=${effectiveCustomerId}`
      );
      if (!res.ok) throw new Error('Failed to load menu for pickup location.');
      const data = await res.json();
      const payload = data.data || data;

      if (payload.business || payload.banners) {
        const merged = extractBusinessPayload(payload);
        setAppCurrencySymbol(merged.currency_symbol);
        setBusiness(merged);
      }
      if (payload.loyalty_points !== undefined) setLoyaltyPoints(payload.loyalty_points);

      const newMenu = payload.menu || [];
      setMenu(newMenu);
      validateAndUpdateCart(newMenu);
      if (newMenu.length > 0) setActiveCategory(getFirstCategoryId(newMenu));
    } catch (err) {
      console.error(err);
      alert('Could not update menu for pickup location: ' + err.message);
    } finally {
      setSwitchingLocation(false);
    }
  };

  // Action: Open New Address Modal (starts at Step 1: Map)
  const handleOpenNewAddressModal = () => {
    const defaultLat = business?.latitude ? parseFloat(business.latitude) : 28.5128;
    const defaultLon = business?.longitude ? parseFloat(business.longitude) : 77.0678;

    setAddressStep('map');
    setIsEditingRecipient(false);
    setNewAddressForm((prev) => ({
      house_number: '',
      floor_number: '',
      tower_number: '',
      building_name: '',
      address: '',
      landmark_area: '',
      city: '',
      state: '',
      in_pin_code: '',
      lat: prev.lat || defaultLat,
      lon: prev.lon || defaultLon,
      address_type: 'HOME',
      address_type_label: 'Home',
      recipient_name: customer?.name || '',
      recipient_phone: customer?.phone_number || '',
    }));
    setLocationNotification('');
    setShowAddressModal(false);
    setShowNewAddressModal(true);
  };

  // Leaflet Map Initialization for Step 1
  useEffect(() => {
    if (!showNewAddressModal || addressStep !== 'map') {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
      return;
    }

    const timer = setTimeout(() => {
      if (!mapContainerRef.current) return;

      const curLat = newAddressForm.lat || (business?.latitude ? parseFloat(business.latitude) : 28.5128);
      const curLon = newAddressForm.lon || (business?.longitude ? parseFloat(business.longitude) : 77.0678);

      if (!newAddressForm.lat || !newAddressForm.lon) {
        setNewAddressForm((prev) => ({
          ...prev,
          lat: parseFloat(curLat.toFixed(6)),
          lon: parseFloat(curLon.toFixed(6)),
        }));
      }

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }

      const map = L.map(mapContainerRef.current, {
        center: [curLat, curLon],
        zoom: 16,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      const customPinHtml = `
        <div style="transform: translate(-50%, -100%); display: flex; flex-direction: column; align-items: center; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.35)); cursor: grab;">
          <svg width="34" height="44" viewBox="0 0 24 32" fill="none">
            <path d="M12 0C5.37 0 0 5.37 0 12C0 21 12 32 12 32C12 32 24 21 24 12C24 5.37 18.63 0 12 0Z" fill="#10B981"/>
            <circle cx="12" cy="12" r="5" fill="#ffffff"/>
          </svg>
        </div>
      `;

      const pinIcon = L.divIcon({
        className: 'custom-leaflet-marker',
        html: customPinHtml,
        iconSize: [0, 0],
      });

      const marker = L.marker([curLat, curLon], {
        draggable: true,
        icon: pinIcon,
      }).addTo(map);

      const handleCoordsChange = (lat, lon) => {
        const roundedLat = parseFloat(lat.toFixed(6));
        const roundedLon = parseFloat(lon.toFixed(6));
        setNewAddressForm((prev) => ({
          ...prev,
          lat: roundedLat,
          lon: roundedLon,
        }));

        // Reverse geocoding helper via OpenStreetMap Nominatim
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${roundedLat}&lon=${roundedLon}`)
          .then((res) => res.json())
          .then((data) => {
            if (data && data.address) {
              const road = data.address.road || '';
              const area = data.address.suburb || data.address.neighbourhood || '';
              const fullRoad = [road, area].filter(Boolean).join(', ');
              setNewAddressForm((prev) => ({
                ...prev,
                city: data.address.city || data.address.town || data.address.village || prev.city || '',
                state: data.address.state || prev.state || '',
                in_pin_code: data.address.postcode || prev.in_pin_code || '',
                address: fullRoad || prev.address || '',
                landmark_area: area || prev.landmark_area || '',
              }));
            }
          })
          .catch(() => {});
      };

      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        handleCoordsChange(pos.lat, pos.lng);
      });

      map.on('click', (e) => {
        marker.setLatLng(e.latlng);
        handleCoordsChange(e.latlng.lat, e.latlng.lng);
      });

      mapInstanceRef.current = map;
      markerRef.current = marker;

      // Invalidate size once modal animation stabilizes
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 200);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewAddressModal, addressStep]);

  // Geolocation detector for Step 1
  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      setLocationNotification('Geolocation is not supported by your browser.');
      return;
    }
    setGettingLocation(true);
    setLocationNotification('Fetching your location pin...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const roundedLat = parseFloat(latitude.toFixed(6));
        const roundedLon = parseFloat(longitude.toFixed(6));
        setNewAddressForm((prev) => ({
          ...prev,
          lat: roundedLat,
          lon: roundedLon,
        }));
        setGettingLocation(false);
        setLocationNotification(
          `Location Pin Captured! (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`
        );
        if (mapInstanceRef.current) {
          mapInstanceRef.current.flyTo([roundedLat, roundedLon], 17);
          if (markerRef.current) {
            markerRef.current.setLatLng([roundedLat, roundedLon]);
          }
        }
        // Reverse geocode to autofill
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${roundedLat}&lon=${roundedLon}`)
          .then((res) => res.json())
          .then((data) => {
            if (data && data.address) {
              const road = data.address.road || '';
              const area = data.address.suburb || data.address.neighbourhood || '';
              const fullRoad = [road, area].filter(Boolean).join(', ');
              setNewAddressForm((prev) => ({
                ...prev,
                city: data.address.city || data.address.town || data.address.village || prev.city || '',
                state: data.address.state || prev.state || '',
                in_pin_code: data.address.postcode || prev.in_pin_code || '',
                address: fullRoad || prev.address || '',
                landmark_area: area || prev.landmark_area || '',
              }));
            }
          })
          .catch(() => {});
      },
      () => {
        setGettingLocation(false);
        setLocationNotification(
          'Permission denied. Please tap on the map to set your pin manually.'
        );
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Add New Address - Submit Step 2
  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!newAddressForm.lat || !newAddressForm.lon) {
      alert('Please set your location pin on the map first.');
      setAddressStep('map');
      return;
    }

    try {
      const payload = {
        house_number: newAddressForm.house_number || '',
        floor_number: newAddressForm.floor_number || '',
        tower_number: newAddressForm.tower_number || '',
        building_name: newAddressForm.building_name || '',
        address: newAddressForm.address,
        landmark_area: newAddressForm.landmark_area || '',
        city: newAddressForm.city || '',
        state: newAddressForm.state || '',
        in_pin_code: newAddressForm.in_pin_code || '',
        lat: newAddressForm.lat,
        lon: newAddressForm.lon,
        address_type: newAddressForm.address_type || 'HOME',
        address_type_label:
          newAddressForm.address_type === 'OTHER'
            ? newAddressForm.address_type_label || 'Other'
            : newAddressForm.address_type === 'WORK'
            ? 'Work'
            : 'Home',
        recipient_name: newAddressForm.recipient_name || customer?.name || '',
        recipient_phone: newAddressForm.recipient_phone || customer?.phone_number || '',
      };

      const res = await fetch(
        `${API_BASE}/business/public/customer/${effectiveCustomerId}/address`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error('Failed to save address');
      const rawAddrRes = await res.json();
      const savedAddr = rawAddrRes.data || rawAddrRes;
      setAddresses((prev) => [savedAddr, ...prev]);
      setShowNewAddressModal(false);

      // Automatically select and update delivery menu for this address
      await handleSelectAddress(savedAddr);

      // Reset form
      setNewAddressForm({
        house_number: '',
        floor_number: '',
        tower_number: '',
        building_name: '',
        address: '',
        landmark_area: '',
        city: '',
        state: '',
        in_pin_code: '',
        lat: null,
        lon: null,
        address_type: 'HOME',
        address_type_label: 'Home',
        recipient_name: customer?.name || '',
        recipient_phone: customer?.phone_number || '',
      });
      setIsEditingRecipient(false);
      setAddressStep('map');
      setLocationNotification('');
    } catch (err) {
      alert('Error saving address: ' + err.message);
    }
  };

  // Toggle Read More for Description
  const toggleDescription = (itemId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Item Add Action
  const handleAddItem = (item) => {
    if (business && business.is_open === false) {
      alert(`Store is currently closed: ${business.status_message || 'Not accepting orders'}. You can browse the menu, but ordering is paused.`);
      return;
    }
    if (item.in_stock === false) return;
    const addonGroups = item.add_ons || item.modifier_groups || [];
    const hasMultipleVariants = item.variants && item.variants.length > 1;
    const hasAddons = item.has_addons || addonGroups.length > 0 || hasMultipleVariants;

    const sortedVariants = item.variants && item.variants.length > 0
      ? [...item.variants].sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0))
      : [];

    if (hasAddons) {
      // Open Customization Modal
      setCustomizingItem({ ...item, variants: sortedVariants });
      setSelectedVariant(
        sortedVariants.length > 0 ? sortedVariants[0] : null
      );
      setSelectedAddons([]);
      setHighlightedGroupId(null);
      setShowItemCustomizer(true);
    } else {
      // Direct Add to Cart
      const firstVariant = sortedVariants.length > 0 ? sortedVariants[0] : null;
      addToCart({
        product_id: item.id,
        variant_id: firstVariant ? firstVariant.id : null,
        name: item.name,
        price: item.price,
        markup_price: item.markup_price,
        discount_percentage: item.discount_percentage,
        tax_rate: firstVariant?.tax_rate ?? item.tax_rate ?? business?.total_tax_percentage ?? 5,
        taxes: firstVariant?.taxes || item.taxes || business?.taxes || [],
        quantity: 1,
        addons: [],
        variation_name: '',
        isUnavailable: false,
      });
    }
  };

  // Addon Selection Logic with Min & Max Constraints
  const handleToggleAddon = (group, addon) => {
    if (highlightedGroupId === String(group.group_id || group.group_name)) {
      setHighlightedGroupId(null);
    }
    const minSelect = group.min_select || 0;
    const maxSelect = group.max_select || (minSelect > 0 ? minSelect : 999);

    const groupSelected = selectedAddons.filter((a) => a.group_id === group.group_id);
    const isSelected = groupSelected.some((a) => a.id === addon.id);

    if (maxSelect === 1) {
      if (isSelected) {
        if (minSelect === 0) {
          setSelectedAddons((prev) =>
            prev.filter((a) => !(a.group_id === group.group_id && a.id === addon.id))
          );
        }
      } else {
        const addonWithGroup = { ...addon, group_id: group.group_id, group_name: group.group_name };
        setSelectedAddons((prev) => [
          ...prev.filter((a) => a.group_id !== group.group_id),
          addonWithGroup,
        ]);
      }
    } else {
      if (isSelected) {
        setSelectedAddons((prev) =>
          prev.filter((a) => !(a.group_id === group.group_id && a.id === addon.id))
        );
      } else {
        if (groupSelected.length < maxSelect) {
          const addonWithGroup = { ...addon, group_id: group.group_id, group_name: group.group_name };
          setSelectedAddons((prev) => [...prev, addonWithGroup]);
        }
      }
    }
  };

  // Customizer Validation Check (Min/Max Addon & Variant constraints)
  const getCustomizerValidation = () => {
    if (!customizingItem) return { isValid: false, missingGroups: [], missingVariant: false };

    const addonGroups = customizingItem.add_ons || customizingItem.modifier_groups || [];
    const missingGroups = [];

    for (const group of addonGroups) {
      const minSelect = group.min_select || 0;
      const maxSelect = group.max_select || (minSelect > 0 ? minSelect : 999);
      const groupSelectedCount = selectedAddons.filter(
        (a) => a.group_id === group.group_id
      ).length;

      if (groupSelectedCount < minSelect || groupSelectedCount > maxSelect) {
        missingGroups.push({
          group_id: group.group_id,
          group_name: group.group_name,
          min_select: minSelect,
          max_select: maxSelect,
          selected_count: groupSelectedCount,
        });
      }
    }

    const isVariantValid =
      !customizingItem.variants ||
      customizingItem.variants.length <= 1 ||
      selectedVariant !== null;

    const isValid = isVariantValid && missingGroups.length === 0;

    return { isValid, missingGroups, missingVariant: !isVariantValid };
  };

  const getCustomizerTotalPrice = () => {
    if (!customizingItem) return 0;
    const basePrice = selectedVariant ? selectedVariant.price : customizingItem.price;
    const addonsTotal = selectedAddons.reduce(
      (sum, a) => sum + (parseFloat(a.price) || 0),
      0
    );
    return basePrice + addonsTotal;
  };

  const handleConfirmCustomization = () => {
    if (!customizingItem) return;
    const { isValid, missingGroups, missingVariant } = getCustomizerValidation();
    if (!isValid) {
      let targetId = null;
      let targetGroupId = null;

      if (missingVariant) {
        targetId = 'customizer-group-variants';
        targetGroupId = 'variants';
      } else if (missingGroups.length > 0) {
        const firstMissing = missingGroups[0];
        targetGroupId = String(firstMissing.group_id || firstMissing.group_name);
        targetId = `customizer-group-${targetGroupId}`;
      }

      if (targetId) {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setHighlightedGroupId(null);
        setTimeout(() => {
          setHighlightedGroupId(targetGroupId);
        }, 50);

        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedGroupId(null);
        }, 1200);
      }
      return;
    }

    const price = selectedVariant
      ? selectedVariant.price
      : customizingItem.price;
    const variationName = selectedVariant ? selectedVariant.size : '';
    const markupPrice = selectedVariant?.markup_price ?? customizingItem.markup_price ?? null;
    const discountPct = selectedVariant?.discount_percentage ?? customizingItem.discount_percentage ?? 0;

    addToCart({
      product_id: customizingItem.id,
      variant_id: selectedVariant ? selectedVariant.id : null,
      name: customizingItem.name,
      price: price,
      markup_price: markupPrice,
      discount_percentage: discountPct,
      tax_rate: selectedVariant?.tax_rate ?? customizingItem.tax_rate ?? business?.total_tax_percentage ?? 5,
      taxes: selectedVariant?.taxes || customizingItem.taxes || business?.taxes || [],
      quantity: 1,
      addons: selectedAddons,
      variation_name: variationName,
      isUnavailable: false,
    });
    setShowItemCustomizer(false);
    setCustomizingItem(null);
    setHighlightedGroupId(null);
  };

  const addToCart = (newItem) => {
    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex(
        (c) =>
          c.product_id === newItem.product_id &&
          c.variant_id === newItem.variant_id &&
          JSON.stringify(c.addons) === JSON.stringify(newItem.addons)
      );
      if (existingIndex > -1) {
        const updated = [...prevCart];
        updated[existingIndex].quantity += 1;
        updated[existingIndex].isUnavailable = false;
        return updated;
      }
      return [...prevCart, newItem];
    });
  };

  const updateCartQty = (index, delta) => {
    setCart((prevCart) => {
      const updated = [...prevCart];
      const newQty = updated[index].quantity + delta;
      if (newQty <= 0) {
        updated.splice(index, 1);
      } else {
        updated[index].quantity = newQty;
      }
      return updated;
    });
  };

  // Cart Calculations (excluding unavailable items)
  const getItemSubtotal = (item) => {
    const addonsTotal = (item.addons || []).reduce(
      (sum, a) => sum + (parseFloat(a.price) || 0),
      0
    );
    return (item.price + addonsTotal) * item.quantity;
  };

  const availableCart = cart.filter((item) => !item.isUnavailable);
  const unavailableCart = cart.filter((item) => item.isUnavailable);
  const hasUnavailableItems = unavailableCart.length > 0;

  const cartSubtotal = Math.round(availableCart.reduce((sum, item) => sum + getItemSubtotal(item), 0) * 100) / 100;

  // Markup Subtotal Calculation (Total original price before item discounts)
  const cartMarkupSubtotal = Math.round(availableCart.reduce((sum, item) => {
    const addonsTotal = (item.addons || []).reduce(
      (aSum, a) => aSum + (parseFloat(a.price) || 0),
      0
    );
    const itemMarkup = (parseFloat(item.markup_price) > parseFloat(item.price))
      ? parseFloat(item.markup_price)
      : parseFloat(item.price);
    return sum + (itemMarkup + addonsTotal) * item.quantity;
  }, 0) * 100) / 100;
  const hasMarkupDiscount = cartMarkupSubtotal > cartSubtotal;

  // Delivery Mode and Minimum Delivery Check
  const isDeliveryMode = deliveryType === 'delivery';
  const minDeliveryAmount = business?.min_delivery_amount ?? business?.minimum_delivery_amount ?? business?.min_delivery_price ?? 599;
  const isMinDeliveryUnfulfilled = isDeliveryMode && cartSubtotal > 0 && cartSubtotal < minDeliveryAmount;
  const remainingForDelivery = isMinDeliveryUnfulfilled ? (minDeliveryAmount - cartSubtotal) : 0;

  // Discount Rule Check
  const discountRule = business?.discount_rule && business.discount_rule.is_active !== false ? business.discount_rule : null;
  const minOrderValue = discountRule ? parseFloat(discountRule.min_order_value || 0) : 0;
  const discountValue = discountRule ? parseFloat(discountRule.discount_value || 0) : 0;
  const isDiscountUnlocked = Boolean(discountRule && cartSubtotal >= minOrderValue);
  const flatDiscount = isDiscountUnlocked ? discountValue : 0;
  const remainingForDiscount = (discountRule && !isDiscountUnlocked) ? Math.max(0, minOrderValue - cartSubtotal) : 0;

  // Delivery Fee Check (Requirement 3: if < 35, cross 35 and show actual with saving)
  const deliveryPrice = isDeliveryMode ? (business?.delivery_price !== undefined ? parseFloat(business.delivery_price) : 0) : 0;
  const isDeliveryDiscounted = isDeliveryMode && deliveryPrice < 35;
  const deliverySavings = isDeliveryDiscounted ? (35 - deliveryPrice) : 0;

  // Loyalty Discount
  const maxLoyaltyDiscount = cartSubtotal * 0.1;
  const loyaltyDiscount = useLoyalty
    ? Math.min(maxLoyaltyDiscount, loyaltyPoints)
    : 0;
  const totalDiscount = flatDiscount + loyaltyDiscount;

  // Mixed taxes calculation with proportional discount allocation
  const taxComponentMap = {};

  availableCart.forEach((item) => {
    const itemSubtotal = getItemSubtotal(item);
    const itemDiscountShare = cartSubtotal > 0
      ? (itemSubtotal / cartSubtotal) * totalDiscount
      : 0;
    const itemTaxableBase = Math.max(0, itemSubtotal - itemDiscountShare);

    const itemTaxes = (Array.isArray(item.taxes) && item.taxes.length > 0)
      ? item.taxes
      : (item.tax_rate !== undefined
          ? [{ id: 0, name: 'GST', rate: item.tax_rate, type: 'percent' }]
          : (Array.isArray(business?.taxes) && business.taxes.length > 0
              ? business.taxes
              : [{ id: 0, name: 'GST', rate: business?.total_tax_percentage || 5.0, type: 'percent' }]));

    itemTaxes.forEach((t) => {
      const rateVal = parseFloat(t.rate) || 0;
      const typeStr = (t.type || 'percent').toLowerCase();
      let taxPart = 0;
      if (typeStr === 'percent' || typeStr === 'p') {
        taxPart = (itemTaxableBase * rateVal) / 100.0;
      } else {
        taxPart = rateVal * (item.quantity || 1);
      }

      const key = `${t.name}_${rateVal}_${typeStr}`;
      if (!taxComponentMap[key]) {
        taxComponentMap[key] = {
          id: t.id,
          name: t.name,
          rate: rateVal,
          type: typeStr,
          amount: 0,
        };
      }
      taxComponentMap[key].amount += taxPart;
    });
  });

  let totalTaxAmount = 0;
  Object.values(taxComponentMap).forEach((t) => {
    const roundedAmt = Math.round(t.amount * 100) / 100;
    totalTaxAmount += roundedAmt;
  });
  totalTaxAmount = Math.round(totalTaxAmount * 100) / 100;

  // Grand Total
  const grandTotal = Math.max(
    0,
    Math.round((cartSubtotal - totalDiscount + (isDeliveryMode ? deliveryPrice : 0) + totalTaxAmount) * 100) / 100
  );
  const totalCartCount = availableCart.reduce((sum, item) => sum + item.quantity, 0);

  // Total Savings Calculation:
  // 1. Item savings: (markup_price - price) * quantity
  // 2. Flat discount rule savings
  // 3. Delivery fee savings (35 - deliveryPrice if < 35)
  // 4. Loyalty points discount
  const itemSavings = availableCart.reduce((sum, item) => {
    const markup = parseFloat(item.markup_price) || 0;
    const price = parseFloat(item.price) || 0;
    if (markup > price) {
      return sum + (markup - price) * item.quantity;
    }
    return sum;
  }, 0);

  const totalSavings = Math.round((itemSavings + flatDiscount + deliverySavings + loyaltyDiscount) * 100) / 100;

  // Floating banner on product page above View Cart CTA:
  // Rule 3: "Note this banner should come first on the product page as well as cart page and once this requirement is fullfilled, then only the discount banner should come"
  let floatingBannerText = null;
  let floatingBannerType = null; // 'delivery' or 'discount'
  if (isMinDeliveryUnfulfilled) {
    floatingBannerType = 'delivery';
    floatingBannerText = `Add ${formatRupee(remainingForDelivery)} more for delivery (Min order ${formatRupee(minDeliveryAmount)})`;
  } else if (discountRule) {
    floatingBannerType = 'discount';
    if (!isDiscountUnlocked) {
      floatingBannerText = `Add ${formatRupee(remainingForDiscount)} more to get Flat ${formatRupee(discountValue)} off!`;
    } else {
      floatingBannerText = `🎉 Flat ${formatRupee(discountValue)} discount applied!`;
    }
  }

  const removeUnavailableItems = () => {
    setCart((prevCart) => prevCart.filter((item) => !item.isUnavailable));
  };

  // Place Order Action
  const handlePlaceOrder = async () => {
    if (business && business.is_open === false) {
      alert(`Store is currently closed: ${business.status_message || 'Not accepting orders'}. Ordering is paused.`);
      return;
    }

    if (deliveryType === 'delivery' && business && business.is_serviceable === false) {
      alert(`Selected address is outside our delivery area (${business.distance_km ? `${business.distance_km} km away` : 'out of range'}, delivery available up to ${business.max_delivery_radius_km || 15} km). Please switch to Pick Up.`);
      return;
    }

    if (deliveryType === 'delivery' && !selectedAddress) {
      alert('Please select or add a delivery address.');
      setModalTab('delivery');
      setShowAddressModal(true);
      return;
    }

    if (hasUnavailableItems) {
      alert('Please remove unavailable items before placing your order.');
      return;
    }

    if (availableCart.length === 0) {
      alert('Your cart is empty.');
      return;
    }

    if (isMinDeliveryUnfulfilled) {
      alert(`Minimum delivery order is ${formatRupee(minDeliveryAmount)}. Please add ${formatRupee(remainingForDelivery)} more to proceed.`);
      return;
    }

    setPlacingOrder(true);
    setPaymentError(null);
    try {
      const targetBizId = selectedBusinessId || initialBusinessId || business?.id;
      const targetBrandId = effectiveBrandId || effectiveBrandIdParam;

      // Note: order_type is 'H' for Home Delivery, 'P' for Pickup / Parcel
      const payload = {
        business_id: targetBizId ? parseInt(targetBizId, 10) : undefined,
        brand_id: targetBrandId ? parseInt(targetBrandId, 10) : undefined,
        customer_id: parseInt(effectiveCustomerId, 10),
        customer_name: customer?.name || 'Valued Customer',
        phone_number: customer?.phone_number || undefined,
        delivery_type: deliveryType,
        order_type: deliveryType === 'delivery' ? 'H' : 'P',
        address_id: deliveryType === 'delivery' && selectedAddress ? selectedAddress.id : null,
        address_details: deliveryType === 'delivery' ? selectedAddress : null,
        items: availableCart,
        notes: orderNotes,
        use_loyalty_points: useLoyalty,
        discount_amount: loyaltyDiscount + flatDiscount,
        flat_discount: flatDiscount,
        delivery_fee: isDeliveryMode ? deliveryPrice : 0,
        tax_total: totalTaxAmount,
        total_amount: grandTotal,
      };

      const res = await fetch(`${API_BASE}/business/public/delivery_order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.errors || errJson.message || 'Failed to place order');
      }

      const resJson = await res.json();
      const orderData = resJson.data || resJson;

      // Direct Razorpay Integration
      if (orderData.payment_method === 'razorpay' && orderData.razorpay) {
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          setPlacingOrder(false);
          setPaymentError('Could not load Razorpay payment gateway. Please check your internet connection and retry.');
          return;
        }

        const options = {
          key: orderData.razorpay.key_id || business?.razorpay_key_id,
          amount: orderData.razorpay.amount,
          currency: orderData.razorpay.currency || 'INR',
          name: orderData.razorpay.business_name || business?.name || 'Order Payment',
          description: `Order #${orderData.order_id}`,
          order_id: orderData.razorpay.order_id,
          prefill: {
            name: customer?.name || '',
            contact: customer?.phone_number || '',
          },
          notes: {
            order_id: String(orderData.order_id),
            business_id: String(targetBizId || ''),
          },
          theme: {
            color: business?.brand_theme_color || '#10b981',
          },
          handler: async function (response) {
            setPlacingOrder(true);
            try {
              const verifyRes = await fetch(`${API_BASE}/business/public/razorpay/verify_payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  order_id: orderData.order_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });

              if (!verifyRes.ok) {
                const errJson = await verifyRes.json().catch(() => ({}));
                throw new Error(errJson.detail || errJson.message || 'Payment signature verification failed');
              }

              const verifyJson = await verifyRes.json();
              const confirmed = verifyJson.data || verifyJson;

              setCart([]);
              setConfirmedOrder({
                order_id: confirmed.order_id || orderData.order_id,
                amount_paid: confirmed.amount_paid !== undefined ? confirmed.amount_paid : grandTotal,
                delivery_type: confirmed.delivery_type || deliveryType,
                address: confirmed.address || selectedAddress,
                items: confirmed.items || availableCart,
              });
              setOrderSuccess(true);
              setPlacingOrder(false);
            } catch (vErr) {
              console.error('Payment confirmation error:', vErr);
              setPaymentError(vErr.message || 'Payment was received but verification failed. Please contact support.');
              setPlacingOrder(false);
            }
          },
          modal: {
            ondismiss: function () {
              setPlacingOrder(false);
              setPaymentError('Payment was not completed. Your cart is preserved — please click retry to complete payment.');
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (failResp) {
          console.error('Razorpay payment failed:', failResp.error);
          setPlacingOrder(false);
          setPaymentError(failResp.error?.description || 'Payment was unsuccessful. Please retry payment.');
        });
        rzp.open();
        return;
      }

      // Fallback: Legacy WhatsApp checkout
      setOrderSuccess(true);
      setTimeout(() => {
        window.close();
        const waPhone = business?.whatsapp_phone_number
          ? String(business.whatsapp_phone_number).replace(/\D/g, '')
          : '';
        window.location.href = waPhone ? `https://wa.me/${waPhone}` : 'https://wa.me/';
      }, 500);
    } catch (err) {
      alert('Error placing order: ' + err.message);
      setPlacingOrder(false);
    }
  };

  // Filtered Menu Items (supports parent_category grouping and standalone categories)
  const filteredMenu = useMemo(() => {
    const query = (searchQuery || '').trim().toLowerCase();

    return (menu || [])
      .map((entry) => {
        if (entry.type === 'parent_category') {
          const childCats = entry.categories || [];
          const filteredChildCats = childCats
            .map((subCat) => {
              const matchingItems = (subCat.items || []).filter((item) => {
                if (!query) return true;
                return (
                  item.name.toLowerCase().includes(query) ||
                  (item.description && item.description.toLowerCase().includes(query)) ||
                  (subCat.name && subCat.name.toLowerCase().includes(query)) ||
                  (entry.name && entry.name.toLowerCase().includes(query))
                );
              });
              return { ...subCat, items: matchingItems };
            })
            .filter((subCat) => subCat.items.length > 0);

          const matchingDirectItems = (entry.items || []).filter((item) => {
            if (!query) return true;
            return (
              item.name.toLowerCase().includes(query) ||
              (item.description && item.description.toLowerCase().includes(query)) ||
              (entry.name && entry.name.toLowerCase().includes(query))
            );
          });

          const allItems = filteredChildCats.length > 0
            ? filteredChildCats.flatMap((c) => c.items)
            : matchingDirectItems;

          return {
            ...entry,
            categories: filteredChildCats,
            items: allItems,
          };
        } else {
          const matchingItems = (entry.items || []).filter((item) => {
            if (!query) return true;
            return (
              item.name.toLowerCase().includes(query) ||
              (item.description && item.description.toLowerCase().includes(query)) ||
              (entry.name && entry.name.toLowerCase().includes(query))
            );
          });
          return { ...entry, items: matchingItems };
        }
      })
      .filter((entry) => {
        if (entry.type === 'parent_category') {
          return (entry.categories && entry.categories.length > 0) || (entry.items && entry.items.length > 0);
        }
        return entry.items && entry.items.length > 0;
      });
  }, [menu, searchQuery]);

  // Track active category on scroll so floating menu always reflects current scroll position
  useEffect(() => {
    const categoryBlocks = document.querySelectorAll('.category-block');
    if (!categoryBlocks || categoryBlocks.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((e) => e.isIntersecting);
        if (visibleEntries.length > 0) {
          visibleEntries.sort(
            (a, b) => Math.abs(a.boundingClientRect.top - 60) - Math.abs(b.boundingClientRect.top - 60)
          );
          const topBlock = visibleEntries[0];
          const match = topBlock.target.id.match(/^cat-block-(.+)$/);
          if (match) {
            const catId = isNaN(match[1]) ? match[1] : Number(match[1]);
            setActiveCategory(catId);
          }
        }
      },
      {
        rootMargin: '-60px 0px -50% 0px',
        threshold: [0, 0.2, 0.5],
      }
    );

    categoryBlocks.forEach((block) => observer.observe(block));
    return () => observer.disconnect();
  }, [filteredMenu]);

  // Reusable Item Card Renderer
  const renderItemCard = (item) => {
    const cartEntries = cart.filter((c) => c.product_id === item.id);
    const cartQty = cartEntries.reduce((s, i) => s + i.quantity, 0);
    const isExpanded = !!expandedDescriptions[item.id];
    const desc = item.description || '';
    const isLongDesc = desc.length > 70;
    const isOutOfStock = item.in_stock === false;
    const pricing = getItemPricingInfo(item);

    return (
      <div
        key={item.id}
        className={`item-card ${isOutOfStock ? 'out-of-stock-card' : ''}`}
        onClick={() => setSelectedProductDetail(item)}
      >
        {/* Left Details */}
        <div className="item-left">
          {renderDietaryIndicator(item)}

          {item.tags && item.tags.length > 0 && (
            <span className="bestseller-tag">{item.tags[0]}</span>
          )}

          <div className="item-title">{item.name}</div>

          <div className="item-price-row">
            <span className="item-price">{formatRupee(pricing.price)}</span>
            {pricing.hasDiscount && (
              <>
                <span className="item-markup-price">{formatRupee(pricing.markupPrice)}</span>
                <span className="item-discount-percentage">{pricing.discountPercentage}% OFF</span>
              </>
            )}
          </div>

          {desc && (
            <div className="item-desc">
              {isLongDesc && !isExpanded
                ? `${desc.slice(0, 70)}... `
                : `${desc} `}
              {isLongDesc && (
                <span
                  className="read-more-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDescription(item.id);
                  }}
                >
                  {isExpanded ? 'Read less' : 'Read more'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right Image & Action Button */}
        <div className="item-right">
          {item.image ? (
            <img
              src={item.image}
              alt={item.name}
              className="item-img"
              loading="lazy"
            />
          ) : (
            <div className="item-img-placeholder">🍽️</div>
          )}

          <div
            className={`add-btn-wrapper ${business && business.is_open === false ? 'disabled-wrapper' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            {isOutOfStock ? (
              <div className="out-of-stock-pill">Out of Stock</div>
            ) : business && business.is_open === false ? (
              <button
                type="button"
                className="add-main-btn disabled-btn-faded"
                disabled
                aria-disabled="true"
                title={formatNextOpenText(business.next_open_at, business.status_message)}
              >
                ADD <span>+</span>
              </button>
            ) : cartQty > 0 ? (
              <div className="qty-stepper">
                <button
                  className="stepper-btn"
                  onClick={() => {
                    const idx = cart.findIndex((c) => c.product_id === item.id);
                    if (idx > -1) updateCartQty(idx, -1);
                  }}
                >
                  -
                </button>
                <span className="stepper-qty">{cartQty}</span>
                <button
                  className="stepper-btn"
                  onClick={() => handleAddItem(item)}
                >
                  +
                </button>
              </div>
            ) : (
              <button
                className="add-main-btn"
                onClick={() => handleAddItem(item)}
              >
                ADD <span>+</span>
              </button>
            )}
          </div>

          {!isOutOfStock &&
            (item.has_addons ||
              (item.add_ons && item.add_ons.length > 0) ||
              (item.modifier_groups && item.modifier_groups.length > 0) ||
              (item.variants && item.variants.length > 1)) && (
              <span className={`customizable-label ${business && business.is_open === false ? 'muted' : ''}`}>Customizable</span>
            )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="delivery-app dop" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div className="modal-title">Loading Menu & Offers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="delivery-app dop" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <h3 style={{ color: '#dc2626' }}>Error</h3>
        <p style={{ margin: '12px 0', color: '#64748b' }}>{error}</p>
        <button className="primary-btn" onClick={fetchData} style={{ width: 'auto', padding: '10px 24px' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="delivery-app dop">
      {/* ── Top Header: Order Type, Delivery Address (50-70%), Loyalty Points & User Profile ── */}
      <div className={`top-header-bar ${headerHidden ? 'hidden' : ''}`}>
        <div className="header-row">
          <div
            className="top-address-bar"
            onClick={() => {
              setModalTab(deliveryType);
              setShowAddressModal(true);
            }}
            title="Click to change order type, delivery address, or pickup store"
          >
            <span className="address-icon">
              {deliveryType === 'delivery' ? '🛵' : '🛍️'}
            </span>
            <div className="address-text-box">
              <span className="address-label">
                {deliveryType === 'delivery' ? `Deliver in ${formatDeliveryTimeRange(business?.delivery_time_in_minutes)}` : 'Pickup From'}
                <span className="header-dropdown-indicator">▾</span>
              </span>
              <span className="address-value">
                {deliveryType === 'delivery'
                  ? getHeaderDeliveryAddress(selectedAddress)
                  : selectedPickupLocation?.name || business?.name || 'Select Store'}
              </span>
            </div>
          </div>

          <div className="top-right-bar">
            <div className="header-loyalty-badge" title={`You have ${loyaltyPoints || 0} Loyalty Points`}>
              <span className="coin-icon">🪙</span>
              <span className="loyalty-points-num">{loyaltyPoints || 0}</span>
              <span className="loyalty-points-sub">pts</span>
            </div>

            <div
              className="header-profile-avatar"
              title={customer?.name ? `User: ${customer.name}` : 'User Profile'}
            >
              {customer?.name ? customer.name.trim().charAt(0).toUpperCase() : '👤'}
            </div>
          </div>
        </div>
      </div>

      {/* ── Unserviceable Delivery Address Banner ── */}
      {deliveryType === 'delivery' && business && business.is_serviceable === false && (
        <div className="unserviceable-notice-bar">
          <div className="unserviceable-badge">
            <span className="unserviceable-icon">📍</span>
            <div className="unserviceable-text-group">
              <span className="unserviceable-title">
                Outside Delivery Area ({business.distance_km ? `${business.distance_km} km away` : 'out of zone'})
              </span>
              <span className="unserviceable-subtitle">
                Delivery is available up to {business.max_delivery_radius_km || 15} km. Please switch to Pick Up or change your address.
              </span>
            </div>
          </div>
          <div className="unserviceable-actions">
            <button
              type="button"
              className="unserviceable-btn-pickup"
              onClick={() => {
                setDeliveryType('pickup');
                if (pickupLocations.length > 0) {
                  setSelectedPickupLocation(pickupLocations[0]);
                  setSelectedBusinessId(pickupLocations[0].id);
                }
              }}
            >
              🛍️ Switch to Pick Up
            </button>
            <button
              type="button"
              className="unserviceable-btn-change"
              onClick={() => {
                setModalTab('delivery');
                setShowAddressModal(true);
              }}
            >
              Change Address
            </button>
          </div>
        </div>
      )}

      {/* ── Promotional Banners Carousel (Above Search Bar) ── */}
      {banners.length > 0 && (
        <div className="banners-section">
          <div className="banner-carousel-container">
            <div
              className="banner-track"
              style={{
                transform: `translateX(-${currentBannerIndex * 100}%)`,
              }}
            >
              {banners.map((banner, idx) => (
                <div
                  key={banner.id || idx}
                  className="banner-slide"
                  onClick={() => handleBannerClick(banner)}
                >
                  <img
                    src={banner.image_url}
                    alt="Promotional Banner"
                    className="banner-image"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {banners.length > 1 && (
              <>
                <button
                  type="button"
                  className="banner-arrow-btn prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentBannerIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
                  }}
                  aria-label="Previous banner"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="banner-arrow-btn next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
                  }}
                  aria-label="Next banner"
                >
                  ›
                </button>

                <div className="banner-dots">
                  {banners.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`banner-dot ${currentBannerIndex === idx ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentBannerIndex(idx);
                      }}
                      aria-label={`Banner slide ${idx + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Sticky Search Header Container ── */}
      <div className="sticky-header-container">
        {/* Search Bar Section */}
        <div className="search-section">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search for delicious dishes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Menu List Section (Zomato Style) ── */}
      <div className="dop-menu-container">
        {filteredMenu.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#64748b' }}>
            No items match your search "{searchQuery}".
          </div>
        ) : (
          filteredMenu.map((entry) => {
            if (entry.type === 'parent_category') {
              const childCats = entry.categories && entry.categories.length > 0
                ? entry.categories
                : [{ id: entry.id, name: null, items: entry.items || [] }];

              return (
                <React.Fragment key={`parent-sec-${entry.id}`}>
                  {childCats.map((subCat) => (
                    <div
                      key={`cat-block-${subCat.id}`}
                      id={`cat-block-${subCat.id}`}
                      className="category-block"
                    >
                      <div className="category-header sticky-category-header">
                        <div className="category-header-title-wrap">
                          <span className="parent-category-title-bold">{entry.name}</span>
                          {subCat.name && (
                            <>
                              <span className="category-title-sep"> - </span>
                              <span className="category-title-light">{subCat.name}</span>
                            </>
                          )}
                        </div>
                        <span className="item-count-badge">{(subCat.items || []).length} items</span>
                      </div>

                      {(subCat.items || []).map((item) => renderItemCard(item))}
                    </div>
                  ))}
                </React.Fragment>
              );
            }

            // Standalone Category (without parent category)
            return (
              <div key={`cat-block-${entry.id}`} id={`cat-block-${entry.id}`} className="category-block">
                <div className="category-header sticky-category-header">
                  <div className="category-header-title-wrap">
                    <span className="parent-category-title-bold">{entry.name}</span>
                  </div>
                  <span className="item-count-badge">{(entry.items || []).length} items</span>
                </div>

                {(entry.items || []).map((item) => renderItemCard(item))}
              </div>
            );
          })
        )}
      </div>

      {/* ── Outlet Closed Sticky Chip at Bottom ── */}
      {business && business.is_open === false && (
        <div className="outlet-closed-sticky-chip-container">
          <div className="outlet-closed-sticky-chip">
            <span className="closed-chip-icon">🕒</span>
            <span className="closed-chip-text">
              {formatNextOpenText(business.next_open_at, business.status_message)}
            </span>
          </div>
        </div>
      )}

      {/* ── Floating Category Menu Popover Modal & Backdrop ── */}
      {showCategoryBook && (
        <>
          <div
            className="floating-menu-backdrop"
            onClick={() => setShowCategoryBook(false)}
          />
          <div
            className={`floating-menu-modal ${cart.length > 0 ? 'with-cart-offset' : (business && business.is_open === false ? 'with-chip-offset' : '')}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="floating-menu-modal-header">
              <div className="floating-menu-header-title-wrap">
                <span className="floating-menu-modal-title">Menu</span>
                <span className="floating-menu-badge-count">{totalMenuItemsCount} items</span>
              </div>
              <button
                className="floating-menu-close-btn"
                onClick={() => setShowCategoryBook(false)}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            <div className="floating-menu-modal-body">
              {menu.map((entry) => {
                if (entry.type === 'parent_category') {
                  const isExpanded = isModalParentExpanded(entry.id);
                  const childCats = entry.categories && entry.categories.length > 0
                    ? entry.categories
                    : [{ id: entry.id, name: entry.name, items: entry.items || [] }];
                  const totalItems = childCats.reduce((sum, c) => sum + (c.items?.length || 0), 0);

                  return (
                    <div key={`modal-parent-${entry.id}`} className="float-parent-group">
                      <div
                        className={`float-parent-row ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleModalParent(entry.id)}
                      >
                        <div className="float-row-left">
                          <span className="float-parent-name">{entry.name}</span>
                          <span className="float-expand-indicator">
                            {isExpanded ? '−' : '+'}
                          </span>
                        </div>
                        <span className="float-row-count">{totalItems}</span>
                      </div>

                      {isExpanded && (
                        <div className="float-subcat-container">
                          {childCats.map((subCat) => {
                            const isCurrentActive = String(activeCategory) === String(subCat.id);
                            const subCatCount = (subCat.items || []).length;
                            return (
                              <div
                                key={subCat.id}
                                className={`float-subcat-row ${isCurrentActive ? 'active' : ''}`}
                                onClick={() => {
                                  setShowCategoryBook(false);
                                  setActiveCategory(subCat.id);
                                  setTimeout(() => {
                                    const el = document.getElementById(`cat-block-${subCat.id}`);
                                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                                  }, 60);
                                }}
                              >
                                <div className="float-subcat-left">
                                  <span className="float-subcat-bullet">•</span>
                                  <span className="float-subcat-name">{subCat.name}</span>
                                </div>
                                <span className="float-row-count">{subCatCount}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Standalone Category (without parent category)
                const isCurrentActive = String(activeCategory) === String(entry.id);
                const count = (entry.items || []).length;
                return (
                  <div
                    key={`modal-cat-${entry.id}`}
                    className={`float-standalone-row ${isCurrentActive ? 'active' : ''}`}
                    onClick={() => {
                      setShowCategoryBook(false);
                      setActiveCategory(entry.id);
                      setTimeout(() => {
                        const el = document.getElementById(`cat-block-${entry.id}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }, 60);
                    }}
                  >
                    <span className="float-standalone-name">{entry.name}</span>
                    <span className="float-row-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ── Floating Category Menu Button ── */}
      <button
        className={`floating-menu-btn ${showCategoryBook ? 'is-open' : ''} ${cart.length > 0 ? 'with-cart-offset' : (business && business.is_open === false ? 'with-chip-offset' : '')}`}
        onClick={handleToggleCategoryMenu}
        aria-label={showCategoryBook ? "Close menu" : "Open menu"}
        aria-expanded={showCategoryBook}
      >
        {showCategoryBook ? (
          <>
            <span className="floating-menu-btn-icon">✕</span>
            <span className="floating-menu-btn-label">CLOSE</span>
          </>
        ) : (
          <>
            <span className="floating-menu-btn-icon">🍽️</span>
            <span className="floating-menu-btn-label">MENU</span>
          </>
        )}
      </button>

      {/* ── Floating Cart Bar at Bottom with Threshold Banner ── */}
      {cart.length > 0 && (
        <div className="floating-cart-wrapper">
          {floatingBannerText && (
            <div className={`floating-threshold-banner ${floatingBannerType === 'delivery' ? 'delivery-threshold' : 'discount-threshold'}`}>
              <span className="banner-icon">{floatingBannerType === 'delivery' ? '🛵' : '🏷️'}</span>
              <span className="banner-text">{floatingBannerText}</span>
            </div>
          )}

          <div className="floating-cart-bar" onClick={() => setShowCartModal(true)}>
            <div className="cart-bar-info">
              <span className="cart-item-count">
                {totalCartCount} ITEMS
                {hasUnavailableItems && ` • ⚠️ ${unavailableCart.length} unavailable`}
              </span>
              <div className="cart-price-savings-row">
                <span className="cart-subtotal">{formatRupee(grandTotal)}</span>
                {totalSavings > 0 && (
                  <span className="cart-savings-badge">
                    Saved {formatRupee(totalSavings)} 🎉
                  </span>
                )}
              </div>
            </div>
            <div className="cart-view-btn">
              View Cart ➔
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
         MODALS & DRAWERS
      ═══════════════════════════════════════════════════════════ */}


      {/* 2. Item Add-on / Customization Modal */}
      {showItemCustomizer && customizingItem && (() => {
        const addonGroups = customizingItem.add_ons || customizingItem.modifier_groups || [];
        const { isValid } = getCustomizerValidation();
        const totalPrice = getCustomizerTotalPrice();

        return (
          <div
            className="modal-overlay"
            onClick={() => {
              setShowItemCustomizer(false);
              setHighlightedGroupId(null);
            }}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <span className="modal-title">Customize "{customizingItem.name}"</span>
                <button
                  className="close-modal-btn"
                  onClick={() => {
                    setShowItemCustomizer(false);
                    setHighlightedGroupId(null);
                  }}
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">
                {/* Variations / Portion Sizes */}
                {customizingItem.variants && customizingItem.variants.length > 1 && (
                  <div
                    id="customizer-group-variants"
                    className={`addon-group-box ${highlightedGroupId === 'variants' ? 'highlight-shake' : ''}`}
                    style={{ marginTop: 0 }}
                  >
                    <div className="addon-group-header">
                      <div className="addon-group-title-row">
                        <span className="addon-group-name">Select Quantity / Portion</span>
                        <span className="addon-badge satisfied">Required</span>
                      </div>
                    </div>
                    <div className="addon-group-items">
                      {(customizingItem.variants || []).slice().sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0)).map((v) => (
                        <div
                          key={v.id}
                          className={`option-row ${selectedVariant?.id === v.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedVariant(v);
                            if (highlightedGroupId === 'variants') {
                              setHighlightedGroupId(null);
                            }
                          }}
                        >
                          <span className="option-label">
                            <span className="addon-name-text">{v.size}</span>
                          </span>
                          <div className="option-right">
                            {parseFloat(v.price) > 0 && (
                              <span className="option-price">{formatRupee(v.price)}</span>
                            )}
                            <input
                              type="radio"
                              name="variant_select"
                              checked={selectedVariant?.id === v.id}
                              onChange={() => {}}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Addons / Modifier Groups */}
                {addonGroups.map((group) => {
                  const minSelect = group.min_select || 0;
                  const maxSelect = group.max_select || (minSelect > 0 ? minSelect : 999);
                  const groupSelected = selectedAddons.filter((a) => a.group_id === group.group_id);
                  const count = groupSelected.length;
                  const isSatisfied = count >= minSelect && count <= maxSelect;
                  const isMaxReached = count >= maxSelect;
                  const isMandatory = minSelect > 0;
                  const groupId = group.group_id || group.group_name;
                  const isHighlighted = highlightedGroupId === String(groupId);

                  let helperText = '';
                  if (maxSelect === 1) {
                    helperText = isMandatory ? 'Select 1 option (Required)' : 'Select up to 1 option';
                  } else if (isMandatory) {
                    helperText = minSelect === maxSelect
                      ? `Select exactly ${minSelect} (${count}/${maxSelect})`
                      : `Select ${minSelect} to ${maxSelect} (${count}/${maxSelect})`;
                  } else {
                    helperText = `Select up to ${maxSelect} (${count}/${maxSelect})`;
                  }

                  return (
                    <div
                      key={groupId}
                      id={`customizer-group-${groupId}`}
                      className={`addon-group-box ${isHighlighted ? 'highlight-shake' : ''}`}
                    >
                      <div className="addon-group-header">
                        <div className="addon-group-title-row">
                          <span className="addon-group-name">{group.group_name}</span>
                          <span className={`addon-badge ${isSatisfied ? 'satisfied' : 'required'}`}>
                            {isSatisfied
                              ? count > 0
                                ? `✓ ${count} selected`
                                : 'Optional'
                              : `Required (${count}/${minSelect})`}
                          </span>
                        </div>
                        <div className="addon-group-subtitle">{helperText}</div>
                      </div>

                      <div className="addon-group-items">
                        {group.items.map((addon) => {
                          const isSelected = selectedAddons.some(
                            (a) => a.id === addon.id && a.group_id === group.group_id
                          );
                          const isDisabled = !isSelected && isMaxReached && maxSelect > 1;

                          return (
                            <div
                              key={addon.id}
                              className={`option-row ${isSelected ? 'selected' : ''} ${
                                isDisabled ? 'disabled' : ''
                              }`}
                              onClick={() => {
                                if (!isDisabled) {
                                  handleToggleAddon(group, addon);
                                }
                              }}
                            >
                              <span className="option-label">
                                <span className="addon-name-text">{addon.name}</span>
                              </span>
                              <div className="option-right">
                                {parseFloat(addon.price) > 0 && (
                                  <span className="option-price">+{formatRupee(addon.price)}</span>
                                )}
                                <input
                                  type={maxSelect === 1 ? 'radio' : 'checkbox'}
                                  name={`addon_group_${group.group_id}`}
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onChange={() => {}}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="customizer-footer">
                <button
                  className={`primary-btn ${!isValid ? 'btn-incomplete' : ''}`}
                  onClick={handleConfirmCustomization}
                >
                  {isValid
                    ? `Add to Cart • ${formatRupee(totalPrice)}`
                    : `Select Required Add-ons • ${formatRupee(totalPrice)}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 3. Order Type & Location Selector Modal */}
      {showAddressModal && (
        <div className="modal-overlay address-modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Select Order Type & Location</span>
              <button className="close-modal-btn" onClick={() => setShowAddressModal(false)}>✕</button>
            </div>

            {/* In-Modal Fulfillment Header Switcher */}
            <div className="modal-fulfillment-tabs">
              <button
                type="button"
                className={`fulfillment-tab-btn ${modalTab === 'delivery' ? 'active' : ''}`}
                onClick={() => setModalTab('delivery')}
              >
                🛵 Delivery
              </button>
              <button
                type="button"
                className={`fulfillment-tab-btn ${modalTab === 'pickup' ? 'active' : ''}`}
                onClick={() => setModalTab('pickup')}
              >
                🛍️ Takeaway / Pickup
              </button>
            </div>

            <div className="modal-body">
              {switchingLocation && (
                <div className="switching-location-bar">
                  <span className="spinner-icon">🔄</span> Updating menu for selected location...
                </div>
              )}

              {modalTab === 'delivery' ? (
                /* Delivery Tab: Saved Addresses */
                <div className="modal-tab-content">
                  <div className="tab-section-header">Deliver to Saved Address</div>
                  {addresses.length === 0 ? (
                    <div style={{ padding: '16px 0', color: '#64748b', fontSize: '0.9rem' }}>
                      No saved addresses found. Please add a new address below.
                    </div>
                  ) : (
                    addresses.map((addr) => {
                      const isSelected = selectedAddress?.id === addr.id && deliveryType === 'delivery';
                      return (
                        <div
                          key={addr.id}
                          className={`address-card-option ${isSelected ? 'active' : ''}`}
                          onClick={() => handleSelectAddress(addr)}
                        >
                          <input
                            type="radio"
                            className="address-radio"
                            checked={isSelected}
                            onChange={() => { }}
                          />
                          <div className="addr-details-box">
                            <div className="addr-title">
                              {addr.address_type && (
                                <span className={`addr-type-tag ${addr.address_type.toLowerCase()}`}>
                                  {addr.address_type === 'HOME'
                                    ? '🏠 Home'
                                    : addr.address_type === 'WORK'
                                    ? '🏢 Work'
                                    : `📍 ${addr.address_type_label || 'Other'}`}
                                </span>
                              )}
                              {addr.house_number ? `Flat ${addr.house_number}, ` : ''}
                              {addr.building_name || addr.address || addr.landmark_area || 'Saved Address'}
                            </div>
                            <div className="addr-full">
                              {[addr.address, addr.landmark_area, addr.city, addr.in_pin_code]
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                            {addr.recipient_phone && (
                              <div className="addr-phone-text">📞 {addr.recipient_phone}</div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}

                  <button
                    type="button"
                    className="add-new-addr-btn"
                    onClick={handleOpenNewAddressModal}
                  >
                    + Add New Address
                  </button>
                </div>
              ) : (
                /* Pickup Tab: Pickup Outlets */
                <div className="modal-tab-content">
                  <div className="tab-section-header">Select Pickup Store Location</div>
                  {pickupLocations.length === 0 ? (
                    <div
                      className={`pickup-location-card ${deliveryType === 'pickup' ? 'active' : ''}`}
                      onClick={() => {
                        setDeliveryType('pickup');
                        setShowAddressModal(false);
                      }}
                    >
                      <input
                        type="radio"
                        className="address-radio"
                        checked={deliveryType === 'pickup'}
                        onChange={() => { }}
                      />
                      <div className="addr-details-box">
                        <div className="addr-title">{business?.name || 'Store'}</div>
                        <div className="addr-full">{business?.location || 'Store Location'}</div>
                      </div>
                    </div>
                  ) : (
                    pickupLocations.map((loc) => {
                      const isSelected = selectedBusinessId === loc.id && deliveryType === 'pickup';
                      return (
                        <div
                          key={loc.id}
                          className={`pickup-location-card ${isSelected ? 'active' : ''}`}
                          onClick={() => handleSelectPickupLocation(loc)}
                        >
                          <input
                            type="radio"
                            className="address-radio"
                            checked={isSelected}
                            onChange={() => { }}
                          />
                          <div className="addr-details-box">
                            <div className="pickup-title-row">
                              <span className="addr-title">{loc.name}</span>
                              {loc.direction_link && (
                                <a
                                  href={loc.direction_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="directions-link-btn"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  📍 Directions ↗
                                </a>
                              )}
                            </div>
                            <div className="addr-full">{loc.location || 'Store Location'}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 4. New Address Modal (2-Step: Map Pin -> Address Details) */}
      {showNewAddressModal && (
        <div className="modal-overlay new-address-modal-overlay" onClick={() => setShowNewAddressModal(false)}>
          <div className="modal-content new-address-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-step-title">
                {addressStep === 'details' && (
                  <button
                    type="button"
                    className="back-step-btn"
                    onClick={() => setAddressStep('map')}
                    title="Go back to pin selection"
                  >
                    ←
                  </button>
                )}
                <div>
                  <span className="modal-title">
                    {addressStep === 'map' ? 'Set Delivery Location' : 'Address & Contact Details'}
                  </span>
                  <span className="modal-step-badge">
                    Step {addressStep === 'map' ? '1 of 2' : '2 of 2'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="close-modal-btn"
                onClick={() => setShowNewAddressModal(false)}
              >
                ✕
              </button>
            </div>

            {addressStep === 'map' ? (
              /* ── Step 1: Leaflet Map Pin Selection ── */
              <div className="modal-body map-step-body">
                <div className="map-instruction-bar">
                  <div className="map-instruction-text">
                    📍 {locationNotification || 'Tap anywhere on the map or drag the pin to set your delivery location'}
                  </div>
                  <button
                    type="button"
                    className="map-locate-btn"
                    onClick={handleDetectLocation}
                    disabled={gettingLocation}
                  >
                    {gettingLocation ? 'Locating...' : '🎯 Locate Me'}
                  </button>
                </div>

                <div className="leaflet-map-wrapper">
                  <div ref={mapContainerRef} className="address-leaflet-map" />
                </div>

                <div className="map-selected-preview-card">
                  <div className="preview-pin-icon">📍</div>
                  <div className="preview-pin-info">
                    <div className="preview-pin-title">
                      {newAddressForm.address || 'Selected Delivery Pin'}
                    </div>
                    <div className="preview-pin-coords">
                      {newAddressForm.lat && newAddressForm.lon
                        ? `Lat: ${newAddressForm.lat.toFixed(5)}, Lon: ${newAddressForm.lon.toFixed(5)}`
                        : 'Move the pin to select location'}
                    </div>
                  </div>
                </div>

                <div className="step-footer-cta">
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={!newAddressForm.lat || !newAddressForm.lon || gettingLocation}
                    onClick={() => setAddressStep('details')}
                  >
                    Confirm Location & Continue ➔
                  </button>
                </div>
              </div>
            ) : (
              /* ── Step 2: Address Verification & Details Form ── */
              <form onSubmit={handleSaveAddress} className="new-address-form-wrapper">
                <div className="modal-body details-step-body">
                  {/* Verification Box: Pin Captured with CTA to change pin */}
                  <div className="pin-verified-box">
                    <div className="pin-verified-info">
                      <div className="pin-verified-badge">
                        <span>✓</span> Location Pin Captured
                      </div>
                      <div className="pin-verified-coords">
                        📍 {newAddressForm.lat?.toFixed(5)}, {newAddressForm.lon?.toFixed(5)}
                      </div>
                      {newAddressForm.address && (
                        <div className="pin-verified-address" title={newAddressForm.address}>
                          {newAddressForm.address}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="change-pin-btn"
                      onClick={() => setAddressStep('map')}
                    >
                      Change Pin ✏️
                    </button>
                  </div>

                  {/* 1 Single Address Field as requested */}
                  <div className="form-group">
                    <label className="form-label">
                      Address Details* <span className="form-label-hint">(House/Flat/Floor, Building, Street, Area)</span>
                    </label>
                    <textarea
                      className="form-input address-single-textarea"
                      placeholder="e.g. Flat 302, 3rd Floor, Green Valley Apts, 100 Feet Road"
                      rows={3}
                      required
                      value={newAddressForm.address}
                      onChange={(e) =>
                        setNewAddressForm({ ...newAddressForm, address: e.target.value })
                      }
                    />
                  </div>

                  {/* Recipient Details: Non-editable view by default with Edit icon */}
                  <div className="form-group">
                    <label className="form-label">Recipient Details*</label>
                    {!isEditingRecipient ? (
                      <div className="recipient-view-card">
                        <div className="recipient-view-info">
                          <div className="recipient-view-row">
                            <span className="recipient-view-icon">👤</span>
                            <span className="recipient-view-text recipient-name">
                              {newAddressForm.recipient_name || customer?.name || 'Customer'}
                            </span>
                          </div>
                          <div className="recipient-view-row">
                            <span className="recipient-view-icon">📞</span>
                            <span className="recipient-view-text recipient-phone">
                              {newAddressForm.recipient_phone || customer?.phone_number || 'Mobile Number'}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="recipient-edit-icon-btn"
                          onClick={() => setIsEditingRecipient(true)}
                          title="Edit recipient name and phone"
                        >
                          <span className="edit-pencil-icon">✏️</span>
                          <span className="edit-text">Edit</span>
                        </button>
                      </div>
                    ) : (
                      <div className="recipient-edit-card">
                        <div className="recipient-edit-header">
                          <span className="recipient-edit-title">Edit Recipient Details</span>
                          <button
                            type="button"
                            className="recipient-done-btn"
                            onClick={() => setIsEditingRecipient(false)}
                          >
                            ✓ Done
                          </button>
                        </div>
                        <div className="form-row-2">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-sublabel">Recipient Name</label>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="Recipient Name"
                              value={newAddressForm.recipient_name}
                              onChange={(e) =>
                                setNewAddressForm({
                                  ...newAddressForm,
                                  recipient_name: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-sublabel">Phone Number*</label>
                            <input
                              type="tel"
                              className="form-input"
                              placeholder="10-digit mobile number"
                              required
                              value={newAddressForm.recipient_phone}
                              onChange={(e) =>
                                setNewAddressForm({
                                  ...newAddressForm,
                                  recipient_phone: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Address Type Selector: Home, Work, Other */}
                  <div className="form-group">
                    <label className="form-label">Save Address As*</label>
                    <div
                      className={`address-type-container ${
                        newAddressForm.address_type === 'OTHER' ? 'other-active' : ''
                      }`}
                    >
                      <div className="address-type-pills-row">
                        <button
                          type="button"
                          className={`type-tag-btn pill-home ${
                            newAddressForm.address_type === 'HOME' ? 'active' : ''
                          }`}
                          onClick={() =>
                            setNewAddressForm({
                              ...newAddressForm,
                              address_type: 'HOME',
                              address_type_label: 'Home',
                            })
                          }
                        >
                          🏠 Home
                        </button>
                        <button
                          type="button"
                          className={`type-tag-btn pill-work ${
                            newAddressForm.address_type === 'WORK' ? 'active' : ''
                          }`}
                          onClick={() =>
                            setNewAddressForm({
                              ...newAddressForm,
                              address_type: 'WORK',
                              address_type_label: 'Work',
                            })
                          }
                        >
                          🏢 Work
                        </button>
                        <button
                          type="button"
                          className={`type-tag-btn pill-other ${
                            newAddressForm.address_type === 'OTHER' ? 'active' : ''
                          }`}
                          onClick={() => {
                            if (newAddressForm.address_type === 'OTHER') {
                              // Unselect Other
                              setNewAddressForm({
                                ...newAddressForm,
                                address_type: 'HOME',
                                address_type_label: 'Home',
                              });
                            } else {
                              setNewAddressForm({
                                ...newAddressForm,
                                address_type: 'OTHER',
                                address_type_label:
                                  newAddressForm.address_type_label === 'Home' ||
                                  newAddressForm.address_type_label === 'Work'
                                    ? ''
                                    : newAddressForm.address_type_label || '',
                              });
                            }
                          }}
                        >
                          <span>📍 Other</span>
                          {newAddressForm.address_type === 'OTHER' && (
                            <span
                              className="pill-unselect-icon"
                              title="Unselect"
                              onClick={(e) => {
                                e.stopPropagation();
                                setNewAddressForm({
                                  ...newAddressForm,
                                  address_type: 'HOME',
                                  address_type_label: 'Home',
                                });
                              }}
                            >
                              ✕
                            </span>
                          )}
                        </button>
                      </div>

                      {/* Expandable input on the right side when OTHER is selected */}
                      {newAddressForm.address_type === 'OTHER' && (
                        <div className="other-input-expandable">
                          <input
                            type="text"
                            className="form-input other-label-input"
                            placeholder="e.g. Gym, Parents, Friend..."
                            autoFocus
                            required
                            value={newAddressForm.address_type_label}
                            onChange={(e) =>
                              setNewAddressForm({
                                ...newAddressForm,
                                address_type_label: e.target.value,
                              })
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Constant Footer CTA */}
                <div className="details-step-footer">
                  <button type="submit" className="primary-btn">
                    Save & Use Address
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* 5. Cart Drawer & Loyalty Points */}
      {showCartModal && (
        <div className="modal-overlay cart-modal-overlay" onClick={() => setShowCartModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Your Order Summary</span>
              <button className="close-modal-btn" onClick={() => setShowCartModal(false)}>✕</button>
            </div>

            {orderSuccess ? (
              <div className="modal-body success-screen">
                {confirmedOrder ? (
                  <div className="rzp-success-container">
                    <div className="rzp-success-icon-wrapper">
                      <div className="rzp-success-icon">✓</div>
                    </div>
                    <div className="rzp-success-badge">Paid Online via Razorpay</div>
                    <h2 className="rzp-success-title">Order Confirmed!</h2>
                    <div className="rzp-order-number">Order #{confirmedOrder.order_id}</div>

                    <div className="rzp-summary-box">
                      <div className="rzp-summary-row">
                        <span className="rzp-summary-label">Amount Paid</span>
                        <span className="rzp-summary-val rzp-highlight">{formatRupee(confirmedOrder.amount_paid)}</span>
                      </div>
                      <div className="rzp-summary-row">
                        <span className="rzp-summary-label">Fulfillment</span>
                        <span className="rzp-summary-val">
                          {confirmedOrder.delivery_type === 'delivery' ? '🛵 Home Delivery' : '🛍️ Pickup / Takeaway'}
                        </span>
                      </div>
                      {confirmedOrder.delivery_type === 'delivery' && confirmedOrder.address && (
                        <div className="rzp-summary-row rzp-address-row">
                          <span className="rzp-summary-label">Delivering To</span>
                          <span className="rzp-summary-val">{getFormattedAddress(confirmedOrder.address)}</span>
                        </div>
                      )}
                      {confirmedOrder.items && confirmedOrder.items.length > 0 && (
                        <div className="rzp-items-breakdown">
                          <div className="rzp-items-title">Items Ordered</div>
                          {confirmedOrder.items.map((it, idx) => (
                            <div key={idx} className="rzp-item-line">
                              <span>{it.quantity}x {it.name}</span>
                              <span>{formatRupee((parseFloat(it.price) || 0) * (it.quantity || 1))}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <p className="rzp-updates-subtext">
                      We’ve received your payment and notified the store! Live preparation and delivery updates will be sent to WhatsApp.
                    </p>

                    <a
                      href={
                        business?.whatsapp_phone_number
                          ? `https://wa.me/${String(business.whatsapp_phone_number).replace(/\D/g, '')}`
                          : 'https://wa.me/'
                      }
                      className="whatsapp-success-cta-btn"
                    >
                      <span className="wa-btn-icon">💬</span> Continue to WhatsApp for Live Updates
                    </a>

                    {redirectCountdown !== null && redirectCountdown > 0 && (
                      <div className="rzp-countdown-text">
                        Auto-redirecting to WhatsApp in {redirectCountdown}s...
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="success-icon">🎉</div>
                    <h2>Order Confirmed!</h2>
                    <p style={{ color: '#64748b', marginTop: '8px' }}>
                      We have received your order. Redirecting you back to WhatsApp for payment & updates...
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="modal-body">
                  {/* Payment Error / Retry Banner */}
                  {paymentError && (
                    <div className="payment-error-banner">
                      <div className="payment-error-header">
                        <span className="payment-error-icon">⚠️</span>
                        <div className="payment-error-content">
                          <div className="payment-error-title">Payment Incomplete</div>
                          <div className="payment-error-text">{paymentError}</div>
                        </div>
                      </div>
                      <div className="payment-error-actions">
                        <button
                          type="button"
                          className="retry-payment-inline-btn"
                          onClick={handlePlaceOrder}
                          disabled={placingOrder}
                        >
                          {placingOrder ? 'Processing...' : '🔄 Retry Payment'}
                        </button>
                        <button
                          type="button"
                          className="dismiss-error-btn"
                          onClick={() => setPaymentError(null)}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Delivery / Pickup & Address Card inside Cart Modal */}
                  <div className="cart-delivery-card">
                    <div className="cart-delivery-toggle">
                      <button
                        type="button"
                        className={`toggle-btn ${deliveryType === 'delivery' ? 'active' : ''}`}
                        onClick={() => {
                          setModalTab('delivery');
                          setShowAddressModal(true);
                        }}
                      >
                        🛵 Delivery
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${deliveryType === 'pickup' ? 'active' : ''}`}
                        onClick={() => {
                          setModalTab('pickup');
                          setShowAddressModal(true);
                        }}
                      >
                        🛍️ Pickup
                      </button>
                    </div>

                    <div
                      className="cart-address-box"
                      onClick={() => {
                        setModalTab(deliveryType);
                        setShowAddressModal(true);
                      }}
                    >
                      <div className="cart-address-info">
                        <span className="cart-address-icon">
                          {deliveryType === 'delivery' ? '📍' : '🏪'}
                        </span>
                        <div className="cart-address-text">
                          <div className="cart-address-title">
                            {deliveryType === 'delivery' ? `Delivery Address • ${formatDeliveryTimeRange(business?.delivery_time_in_minutes)}` : 'Pickup Store Location'}
                          </div>
                          <div className="cart-address-val">
                            {deliveryType === 'delivery'
                              ? selectedAddress
                                ? getFormattedAddress(selectedAddress)
                                : 'No address added yet — Tap to add'
                              : selectedPickupLocation?.name || business?.name || 'Store Location'}
                          </div>
                        </div>
                      </div>
                      <button type="button" className="cart-change-addr-btn">
                        CHANGE ▾
                      </button>
                    </div>
                  </div>

                  {/* Delivery / Minimum Order / Discount Offer Callout Banner */}
                  {isMinDeliveryUnfulfilled ? (
                    <div className="cart-threshold-callout delivery-warning">
                      <span className="callout-icon">🛵</span>
                      <div className="callout-text">
                        <span>Add <strong>{formatRupee(remainingForDelivery)}</strong> more for delivery. Minimum order is {formatRupee(minDeliveryAmount)}.</span>
                      </div>
                    </div>
                  ) : discountRule ? (
                    <div className={`cart-threshold-callout ${isDiscountUnlocked ? 'discount-unlocked' : 'discount-pending'}`}>
                      <span className="callout-icon">🏷️</span>
                      <div className="callout-text">
                        {isDiscountUnlocked ? (
                          <span>🎉 <strong>{formatRupee(flatDiscount)} Flat Discount</strong> applied on your order!</span>
                        ) : (
                          <span>Add <strong>{formatRupee(remainingForDiscount)}</strong> more to get <strong>{formatRupee(discountValue)} FLAT OFF</strong>!</span>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* Unavailable Items Banner */}
                  {hasUnavailableItems && (
                    <div className="cart-unavailable-banner">
                      <div className="unavailable-banner-text">
                        ⚠️ <strong>{unavailableCart.length} item{unavailableCart.length > 1 ? 's are' : ' is'} not available</strong> at this location.
                      </div>
                      <button
                        type="button"
                        className="remove-unavailable-cta"
                        onClick={removeUnavailableItems}
                      >
                        Remove
                      </button>
                    </div>
                  )}

                  {/* Item List */}
                  {cart.map((item, idx) => {
                    const isItemUnavailable = item.isUnavailable;

                    return (
                      <div key={idx} className={`cart-item-row ${isItemUnavailable ? 'unavailable-item-row' : ''}`}>
                        <div className="cart-item-left">
                          <div className={`cart-item-name ${isItemUnavailable ? 'disabled-item-name' : ''}`}>
                            {item.name} {item.variation_name ? `(${item.variation_name})` : ''}
                          </div>
                          {item.addons && item.addons.length > 0 && (
                            <div className="cart-item-addons">
                              Add-ons: {item.addons.map((a) => `${a.name}${a.price > 0 ? ` (+${formatRupee(a.price)})` : ''}`).join(', ')}
                            </div>
                          )}
                          {item.markup_price && item.markup_price > item.price && (
                            <div className="cart-item-discount-tag">
                              Saved {formatRupee((item.markup_price - item.price) * item.quantity)} ({item.discount_percentage || Math.round(((item.markup_price - item.price)/item.markup_price)*100)}% OFF)
                            </div>
                          )}
                          {isItemUnavailable && (
                            <div className="item-unavailable-badge">
                              ⚠️ Not available at this location
                            </div>
                          )}
                        </div>

                        <div className="cart-item-right">
                          {isItemUnavailable ? (
                            <button
                              type="button"
                              className="remove-item-pill-btn"
                              onClick={() => updateCartQty(idx, -item.quantity)}
                            >
                              ✕ Remove
                            </button>
                          ) : (
                            <div className="qty-stepper">
                              <button className="stepper-btn" onClick={() => updateCartQty(idx, -1)}>
                                -
                              </button>
                              <span className="stepper-qty">{item.quantity}</span>
                              <button className="stepper-btn" onClick={() => updateCartQty(idx, 1)}>
                                +
                              </button>
                            </div>
                          )}

                          <div className={`cart-item-price-col ${isItemUnavailable ? 'muted-price' : ''}`}>
                            {item.markup_price && parseFloat(item.markup_price) > parseFloat(item.price) && (
                              <span className="cart-item-markup-price">
                                {formatRupee((parseFloat(item.markup_price) + (item.addons || []).reduce((aSum, a) => aSum + (parseFloat(a.price) || 0), 0)) * item.quantity)}
                              </span>
                            )}
                            <span className="cart-item-price">{formatRupee(getItemSubtotal(item))}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Order Instructions (Requirement 8: Button that shows text box on click) */}
                  {!showOrderNotes && !orderNotes ? (
                    <div className="order-note-btn-wrapper">
                      <button
                        type="button"
                        className="order-note-toggle-btn"
                        onClick={() => setShowOrderNotes(true)}
                      >
                        <span className="note-icon">📝</span>
                        <span>Add Cooking / Delivery Instructions</span>
                        <span className="note-plus">+</span>
                      </button>
                    </div>
                  ) : (
                    <div className="order-note-container">
                      <div className="order-note-header">
                        <label className="form-label">Cooking & Delivery Instructions</label>
                        <button
                          type="button"
                          className="order-note-clear-btn"
                          onClick={() => {
                            if (!orderNotes) {
                              setShowOrderNotes(false);
                            } else {
                              setOrderNotes('');
                              setShowOrderNotes(false);
                            }
                          }}
                        >
                          {orderNotes ? '✕ Clear' : '✕ Cancel'}
                        </button>
                      </div>
                      <textarea
                        className="order-note-input"
                        placeholder="e.g. Make it extra spicy, Leave at front door"
                        value={orderNotes}
                        onChange={(e) => setOrderNotes(e.target.value)}
                        autoFocus={showOrderNotes && !orderNotes}
                        rows={3}
                      />
                    </div>
                  )}

                  {/* Loyalty Points Checkbox */}
                  {loyaltyPoints > 0 && (
                    <div className="loyalty-box">
                      <div className="loyalty-left">
                        <input
                          type="checkbox"
                          className="loyalty-checkbox"
                          checked={useLoyalty}
                          onChange={(e) => setUseLoyalty(e.target.checked)}
                        />
                        <div>
                          <div className="loyalty-title">Apply Loyalty Points</div>
                          <div className="loyalty-sub">
                            You have {loyaltyPoints} pts. Save 10% ({formatRupee(loyaltyDiscount)})
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Total Savings Card */}
                  {totalSavings > 0 && (
                    <div className="cart-savings-card">
                      <span className="savings-icon">🎉</span>
                      <div className="savings-text">
                        You are saving <strong>{formatRupee(totalSavings)}</strong> on this order!
                      </div>
                    </div>
                  )}

                  {/* Bill Summary */}
                  <div className="price-summary-card">
                    <div className="summary-row">
                      <span>Item Subtotal</span>
                      {hasMarkupDiscount ? (
                        <span className="subtotal-display">
                          <span className="strikethrough-price">{formatRupee(cartMarkupSubtotal)}</span>
                          <span className="actual-subtotal-price">{formatRupee(cartSubtotal)}</span>
                        </span>
                      ) : (
                        <span>{formatRupee(cartSubtotal)}</span>
                      )}
                    </div>

                    {flatDiscount > 0 && (
                      <div className="summary-row discount">
                        <span>Flat Discount ({discountRule?.description || `Orders above ${formatRupee(minOrderValue)}`})</span>
                        <span>-{formatRupee(flatDiscount)}</span>
                      </div>
                    )}

                    {useLoyalty && loyaltyDiscount > 0 && (
                      <div className="summary-row discount">
                        <span>Loyalty Points Discount (Max 10%)</span>
                        <span>-{formatRupee(loyaltyDiscount)}</span>
                      </div>
                    )}

                    {isDeliveryMode && (
                      <div className="summary-row">
                        <span>Delivery Fee</span>
                        {isDeliveryDiscounted ? (
                          <span className="delivery-fee-display">
                            <span className="strikethrough-fee">{formatRupee(35)}</span>
                            <span className="actual-fee">{deliveryPrice === 0 ? 'FREE' : formatRupee(deliveryPrice)}</span>
                            <span className="fee-saved-badge">Saved {formatRupee(35 - deliveryPrice)}</span>
                          </span>
                        ) : (
                          <span>{formatRupee(deliveryPrice)}</span>
                        )}
                      </div>
                    )}

                    {/* Taxes */}
                    {totalTaxAmount > 0 && (
                      <div className="summary-row">
                        <span>Taxes</span>
                        <span>{formatRupee(totalTaxAmount)}</span>
                      </div>
                    )}

                    <div className="summary-row total">
                      <span>Grand Total</span>
                      <span>{formatRupee(grandTotal)}</span>
                    </div>

                    {totalSavings > 0 && (
                      <div className="summary-row total-savings-highlight">
                        <span>Total Savings</span>
                        <span className="savings-amount">{formatRupee(totalSavings)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="cart-modal-footer">
                  {isDeliveryMode && !selectedAddress ? (
                    <button
                      type="button"
                      className="primary-btn add-address-cta"
                      onClick={handleOpenNewAddressModal}
                    >
                      + Add Delivery Address
                    </button>
                  ) : hasUnavailableItems ? (
                    <button
                      type="button"
                      className="primary-btn warning-cta"
                      onClick={removeUnavailableItems}
                    >
                      Remove Unavailable Items to Order
                    </button>
                  ) : business && business.is_open === false ? (
                    <button
                      type="button"
                      className="primary-btn disabled-delivery-cta"
                      disabled
                    >
                      Store Closed • {business.next_open_at ? `Opens ${business.next_open_at}` : 'Unavailable'}
                    </button>
                  ) : deliveryType === 'delivery' && business && business.is_serviceable === false ? (
                    <button
                      type="button"
                      className="primary-btn warning-cta"
                      onClick={() => {
                        setDeliveryType('pickup');
                        if (pickupLocations.length > 0) {
                          setSelectedPickupLocation(pickupLocations[0]);
                          setSelectedBusinessId(pickupLocations[0].id);
                        }
                      }}
                    >
                      Out of Delivery Area • Switch to Pick Up
                    </button>
                  ) : isMinDeliveryUnfulfilled ? (
                    <button
                      type="button"
                      className="primary-btn disabled-delivery-cta"
                      disabled
                    >
                      Add {formatRupee(remainingForDelivery)} more for delivery
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={handlePlaceOrder}
                      disabled={placingOrder || availableCart.length === 0}
                    >
                      {placingOrder
                        ? 'Processing Payment...'
                        : paymentError
                        ? `Retry Payment • ${formatRupee(grandTotal)}`
                        : `Place Order • ${formatRupee(grandTotal)}`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 6. Product Detail View Modal (Requirement 7: Full image and details with CTA) */}
      {selectedProductDetail && (() => {
        const item = selectedProductDetail;
        const pricing = getItemPricingInfo(item);
        const cartEntries = cart.filter((c) => c.product_id === item.id);
        const cartQty = cartEntries.reduce((s, i) => s + i.quantity, 0);
        const isOutOfStock = item.in_stock === false;
        const hasCustomization =
          item.has_addons ||
          (item.add_ons && item.add_ons.length > 0) ||
          (item.modifier_groups && item.modifier_groups.length > 0) ||
          (item.variants && item.variants.length > 1);

        return (
          <div className="modal-overlay product-detail-overlay" onClick={() => setSelectedProductDetail(null)}>
            <div className="modal-content product-detail-content" onClick={(e) => e.stopPropagation()}>
              <div className="product-detail-hero">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="product-detail-full-img"
                  />
                ) : (
                  <div className="product-detail-img-placeholder">
                    🍽️
                  </div>
                )}
                <button
                  type="button"
                  className="product-detail-close-btn"
                  onClick={() => setSelectedProductDetail(null)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="product-detail-body">
                <div className="product-detail-badges">
                  {renderDietaryIndicator(item)}

                  {item.tags && item.tags.length > 0 && (
                    <span className="bestseller-tag">{item.tags[0]}</span>
                  )}

                  {pricing.hasDiscount && (
                    <span className="item-discount-percentage">
                      {pricing.discountPercentage}% OFF
                    </span>
                  )}
                </div>

                <h2 className="product-detail-title">{item.name}</h2>

                <div className="product-detail-price-row">
                  <span className="product-detail-price">{formatRupee(pricing.price)}</span>
                  {pricing.hasDiscount && (
                    <span className="product-detail-markup-price">{formatRupee(pricing.markupPrice)}</span>
                  )}
                </div>

                {item.description && (
                  <div className="product-detail-desc">
                    {item.description}
                  </div>
                )}

                {hasCustomization && (
                  <div className="product-detail-custom-note">
                    ✨ Customization options available (portion size, add-ons)
                  </div>
                )}
              </div>

              {/* Sticky Modal Bottom CTA */}
              <div className="product-detail-footer">
                {isOutOfStock ? (
                  <button type="button" className="primary-btn disabled-btn" disabled>
                    Out of Stock
                  </button>
                ) : business && business.is_open === false ? (
                  <button type="button" className="primary-btn store-closed-cta disabled-btn-faded" disabled>
                    {hasCustomization ? 'Customize & Add' : 'ADD'} • {formatNextOpenText(business.next_open_at, business.status_message)}
                  </button>
                ) : hasCustomization ? (
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => {
                      setSelectedProductDetail(null);
                      handleAddItem(item);
                    }}
                  >
                    Customize & Add • {formatRupee(pricing.price)}
                  </button>
                ) : cartQty > 0 ? (
                  <div className="product-detail-stepper-row">
                    <div className="qty-stepper large-stepper">
                      <button
                        type="button"
                        className="stepper-btn"
                        onClick={() => {
                          const idx = cart.findIndex((c) => c.product_id === item.id);
                          if (idx > -1) updateCartQty(idx, -1);
                        }}
                      >
                        -
                      </button>
                      <span className="stepper-qty">{cartQty} in cart</span>
                      <button
                        type="button"
                        className="stepper-btn"
                        onClick={() => handleAddItem(item)}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="primary-btn view-cart-direct-btn"
                      onClick={() => {
                        setSelectedProductDetail(null);
                        setShowCartModal(true);
                      }}
                    >
                      View Cart ➔
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => {
                      handleAddItem(item);
                    }}
                  >
                    Add to Cart • {formatRupee(pricing.price)}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
